import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MapPin, Calendar, Users, ArrowLeft, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function ProjectDetail() {
    const { slug } = useParams();
    const q = useQuery({
        queryKey: ["project", slug],
        queryFn: () => api.get(`/projects/${slug}`).then((r) => r.data),
    });
    const p = q.data;

    if (q.isLoading) return <div className="p-20 text-muted-foreground">Loading…</div>;
    if (!p) return <div className="p-20 text-muted-foreground">Project not found.</div>;

    return (
        <div data-testid="project-detail-page">
            <section className="relative">
                <div className="aspect-[16/8] bg-muted overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-5 lg:px-10 py-12 lg:py-16">
                <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
                    <ArrowLeft className="h-4 w-4" /> All projects
                </Link>
                <Badge
                    variant="outline"
                    className={`font-eyebrow text-[10px] rounded-full px-3 mb-4 ${
                        p.status === "ongoing"
                            ? "bg-secondary/10 text-secondary border-secondary/30"
                            : "bg-primary/10 text-primary border-primary/30"
                    }`}
                >
                    {p.status}
                </Badge>
                <h1 className="font-display text-4xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">{p.title}</h1>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 prose-content text-base text-foreground/85">
                        <p className="text-lg leading-relaxed">{p.description}</p>
                        {p.impact_summary && (
                            <div className="mt-8 bg-accent border-l-4 border-secondary p-6">
                                <div className="font-eyebrow text-xs text-secondary mb-2">Impact</div>
                                <p className="font-display text-xl leading-snug">{p.impact_summary}</p>
                            </div>
                        )}
                        {p.gallery?.length > 0 && (
                            <div className="mt-12">
                                <h2 className="font-display text-2xl mb-5">Gallery</h2>
                                <div className="grid grid-cols-2 gap-3">
                                    {p.gallery.map((img, i) => (
                                        <img key={i} src={img} alt="" className="aspect-square object-cover" loading="lazy" />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <aside className="lg:col-span-4">
                        <div className="bg-card border border-border p-6 sticky top-24">
                            <h3 className="font-display text-lg mb-4">Project details</h3>
                            <ul className="space-y-4 text-sm">
                                {p.location && (
                                    <li className="flex gap-3">
                                        <MapPin className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
                                        <div>
                                            <div className="font-eyebrow text-[10px] text-muted-foreground">Location</div>
                                            <div>{p.location}</div>
                                        </div>
                                    </li>
                                )}
                                {p.start_date && (
                                    <li className="flex gap-3">
                                        <Calendar className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
                                        <div>
                                            <div className="font-eyebrow text-[10px] text-muted-foreground">Timeline</div>
                                            <div>{p.start_date} {p.end_date && `→ ${p.end_date}`}</div>
                                        </div>
                                    </li>
                                )}
                                {p.beneficiaries && (
                                    <li className="flex gap-3">
                                        <Users className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
                                        <div>
                                            <div className="font-eyebrow text-[10px] text-muted-foreground">Beneficiaries</div>
                                            <div>{p.beneficiaries}</div>
                                        </div>
                                    </li>
                                )}
                                {p.category && (
                                    <li className="flex gap-3">
                                        <Sparkles className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
                                        <div>
                                            <div className="font-eyebrow text-[10px] text-muted-foreground">Category</div>
                                            <div className="capitalize">{p.category}</div>
                                        </div>
                                    </li>
                                )}
                            </ul>
                            <Link
                                to="/donate"
                                data-testid="project-donate-link"
                                className="mt-6 block text-center bg-secondary text-secondary-foreground py-3 rounded-full font-medium hover:bg-secondary/90"
                            >
                                Support this project
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
